/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import axiosConfig, { ApiRequestType } from 'axiosConfig/axiosConfig';
import { getDefaultStore } from 'jotai';
import { activeQualifyingMetaDataBySeries } from 'jotai/session/atom';
import { LapData, OriginType, QualifyingLapData, SanctioningBodyEnum } from 'models/viewModels';
import { StreamRequestType } from './RSocketObject';
import { RSocketStreamBaseService } from './RSocketStreamBaseService';

const defaultStore = getDefaultStore();
class QualifyingLapDataService extends RSocketStreamBaseService<QualifyingLapData[], LapData> {
  private qualifyingEntries: QualifyingLapData[] = [];
  private shouldFindByGroupOrRunName: boolean = true;
  protected async getStreamHistoryRequest(): Promise<QualifyingLapData[]> {
    const qualifyingMetadata = defaultStore.get(activeQualifyingMetaDataBySeries);
    this.shouldFindByGroupOrRunName = qualifyingMetadata
      ? qualifyingMetadata?.groupsList?.length > 0 || qualifyingMetadata?.totalRounds > 1
      : false;

    if (this.qualifyingEntries.length === 0) {
      this.qualifyingEntries = await axiosConfig(ApiRequestType.MotorsportsCompToolsApi)
        .get('/rest/session/getQualifyingEntriesData', {
          params: { seriesId: this.request.seriesType, eventId: this.request.raceId },
        })
        .then((response) => {
          return response.data as QualifyingLapData[];
        })
        .catch(() => {
          return [];
        });
    }
    return axiosConfig(ApiRequestType.StreamMetricsApi)
      .post('/rest/backfill/lap', this.request)
      .then((response) => {
        const backfillResponse = response.data as LapData[];
        return this.formatBackfillResponse(backfillResponse);
      })
      .catch(() => {
        return [];
      });
  }

  protected onDataCallback = async (response: LapData) => {
    if (this.listeners.size > 0) {
      const prevLapData = [...this.localCache];
      const lapDataIndex = prevLapData.findIndex((item) => this.doesVehicleRunNameMatch(item, response));
      const lapData = this.lapDataToQualifyingLapData(response, false);
      if (lapDataIndex > -1) {
        prevLapData[lapDataIndex] = lapData;
        this.localCache = prevLapData;
      } else {
        this.localCache = [...prevLapData, lapData];
      }
    }
  };
  async start() {
    this.delay = 1000;
    super.start();
  }

  private lapDataToQualifyingLapData = (lapData: LapData, isDatabaseRecord: boolean): QualifyingLapData => {
    return {
      runningPosition: lapData.runningPosition,
      vehicleNumber: lapData.vehicleNumber,
      driverFullName: lapData.driverFullName,
      deltaToTarget: lapData.deltaToTarget,
      lastLapTime: lapData.lastLapTime,
      bestLapTime: lapData.bestLapTime,
      timeOfDay: lapData.timeOfDay,
      runName: this.findQualifyingEntry(lapData)?.runName || lapData.groupName,
      isDatabaseRecord: isDatabaseRecord, // TODO rename to isQualifyingEntryRecord or something.
      vehicleStatus: lapData.vehicleStatus,
      messageType: lapData.messageType,
      isStartFinish: lapData.isStartFinish,
      qualifyingPosition: this.findQualifyingEntry(lapData)?.qualifyingPosition || 999,
      lapsCompleted: lapData.lapsCompleted,
      startFinish: lapData.isStartFinish,
      databaseRecord: isDatabaseRecord,
    };
  };

  private findQualifyingEntry = (lapData: LapData): QualifyingLapData | undefined => {
    return this.qualifyingEntries.find((entry) => this.doesVehicleRunNameMatch(entry, lapData));
  };

  private formatBackfillResponse = (backfillResponse: LapData[]): QualifyingLapData[] => {
    const formattedBackfillResponse = backfillResponse.map((item) => {
      return this.lapDataToQualifyingLapData(item, false);
    });
    return formattedBackfillResponse.length === 0
      ? this.qualifyingEntries
      : this.qualifyingEntries.length === 0
      ? formattedBackfillResponse
      : formattedBackfillResponse.concat(
          this.qualifyingEntries.filter(
            (entry) => !backfillResponse.some((backfillEntry) => this.doesVehicleRunNameMatch(entry, backfillEntry))
          )
        );
  };

  private doesVehicleRunNameMatch = (cachedEntry: QualifyingLapData, lapMessage: LapData): boolean => {
    return this.shouldFindByGroupOrRunName
      ? lapMessage.vehicleNumber === cachedEntry.vehicleNumber &&
          lapMessage.groupName.toUpperCase().includes(cachedEntry.runName.toUpperCase())
      : lapMessage.vehicleNumber === cachedEntry.vehicleNumber;
  };
}

export const qualifyingLapDataServiceCollection: Map<
  SanctioningBodyEnum,
  Map<OriginType, QualifyingLapDataService>
> = new Map(
  (Object.values(SanctioningBodyEnum) as SanctioningBodyEnum[])
    .filter((x) => typeof x === 'number')
    .map((sanctioningBody) => [
      sanctioningBody,
      new Map(
        (Object.values(OriginType) as OriginType[])
          .filter((x) => typeof x === 'number')
          .map((source: OriginType) => [
            source,
            new QualifyingLapDataService({
              route: 'stream.lap',
              connectionType: StreamRequestType.MotorsportsStreamApi,
              originType: OriginType.MULTILOOP,
              sanctioningBody: sanctioningBody,
            }),
          ])
      ),
    ])
);
